import { createElement } from 'react';

import { Column, EditorContext, Row } from '~components/table-view';
import CutoverPairRemoveAction from '~support-at-home/components/config/service-cutover/CutoverPairRemoveAction';

/** Just wide enough for the icon and its hit area, so the two name columns keep the rest. */
const REMOVE_COLUMN_PX = 48;

/**
 * Step 1 of the service cutover assistant - the replacements added so far.
 *
 * Names rather than ids, and both sides on one row, so the list reads as the sentence the provider
 * just built: this service gives way to that one. The names are carried on the row itself because
 * the pickers above have usually moved on to another category by the time this is read.
 *
 * A factory rather than a constant so the shape can vary with the run if it ever needs to. The remove
 * control reaches the builder's own handler through the table's `context`, the way this wizard's
 * other row actions reach theirs - see cutoverBudgets and cutoverPricePlans.
 */
const columnsFactory = (): Column[] => [
  {
    id: 'from_service_name',
    header: 'From',
    caption: 'The Support at Home service being replaced',
    type: 'text',
    classNames: { column: 'sah-cutover-wrap-cell' },
  },
  {
    id: 'to_service_name',
    header: 'To',
    caption: 'The service taking over from the cutover date',
    type: 'text',
    classNames: { column: 'sah-cutover-wrap-cell' },
  },
  {
    id: 'remove',
    // Named for assistive technology but not on screen: an icon column wide enough for the icon has
    // no room for a word, and an empty `th` is an axe failure (empty-table-header) rather than a
    // neutral blank. No caption - that routes the label into a hover tooltip on the header, which
    // puts the text back on screen.
    header: createElement('span', { className: 'visually-hidden' }, 'Remove'),
    type: 'custom',
    style: { width: `${REMOVE_COLUMN_PX}px` },
    renderReadOnly: (_value: any, row: Row, context?: EditorContext) =>
      CutoverPairRemoveAction({
        row,
        onRemovePair: context?.onRemovePair as ((row: Row) => void) | undefined,
      }),
  },
];

export default columnsFactory;
